import React, { useRef, useState } from 'react';
import moment from 'moment';
import { getPlanningDeletionReason } from './planningSchedule';
import { Modal } from 'react-bootstrap';
import { useDrop } from 'react-dnd';
import { parsePlanningTitle, getPlanningManpower } from './planningCalendar';
import { BOARD_SHIFT, clipBoardEvent, createBoardSlots, getBoardWindow, layoutBoardDay, getBoardDayMetrics, moveBoardEvent, resizeBoardEvent } from './planningBoard';
import PlanningBoardJob, { BOARD_DRAG_TYPE } from './PlanningBoardJob.jsx';
import './PlanningBoard.css';

function BoardDay({ date, board, layout, shift, canCreate, onAddJob, onOpenJob, onDeleteJob, onMoveJob, onResizeJob, onError }) {
    const nodeRef = useRef(null);
    const slots = createBoardSlots(date, shift);
    const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
        accept: BOARD_DRAG_TYPE,
        canDrop: item => canCreate && item.machine === board.machine && item.event.metricsPending !== true,
        drop: (item, monitor) => {
            if (!nodeRef.current || monitor.didDrop()) return;
            const point = monitor.getClientOffset();
            const bounds = nodeRef.current.getBoundingClientRect();
            if (!point || !bounds.height) return;
            const initial = monitor.getInitialClientOffset();
            const source = monitor.getInitialSourceClientOffset();
            const grabOffset = item.mode === 'move' && initial && source ? initial.y - source.y : 0;
            const row = Math.max(0, Math.min(slots.length - (item.mode === 'end' ? 0 : 1), Math.round((point.y - bounds.top - grabOffset) / bounds.height * slots.length)));
            const target = getBoardWindow(date, shift).start.add(row * shift.slotMinutes, 'minutes').format('YYYY-MM-DD HH:mm:ss');
            try {
                onError('');
                if (item.mode === 'move' && onMoveJob) onMoveJob(item.event, moveBoardEvent(item.event, target, item.sourceTimestamp));
                else if (onResizeJob) onResizeJob(item.event, resizeBoardEvent(item.event, item.mode, target));
            } catch (error) { onError(error.message); }
        },
        collect: monitor => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
    }), [board.machine, date, shift, canCreate, onMoveJob, onResizeJob, onError]);
    return (
        <div
            ref={node => { nodeRef.current = node; dropRef(node); }}
            className={`planning-board__day${moment(date).day() === 0 ? ' is-sunday' : ''}${isOver && canDrop ? ' is-drop-target' : ''}`}
            data-board-date={date}
        >
            {slots.map(timestamp => canCreate && board.active !== false ? (
                <button key={timestamp} type="button" className="planning-board__slot" aria-label={`Add job on ${board.machine} at ${moment(timestamp).format('DD/MM/YYYY h:mm A')}`} onClick={() => onAddJob && onAddJob(timestamp, board.assignment)} />
            ) : <div key={timestamp} className="planning-board__slot" aria-hidden="true" />)}
            {layout.segments.map(segment => (
                <PlanningBoardJob key={segment.event.oldId ?? segment.event.id ?? segment.event.jobID} segment={segment} machine={board.machine} shift={shift} canEdit={canCreate} onOpenJob={onOpenJob} onDeleteJob={onDeleteJob} onMoveJob={onMoveJob} onResizeJob={onResizeJob} onError={onError} />
            ))}
        </div>
    );
}

function MachineBoard({ board, week, shift, onOverflow, ...actions }) {
    const dayLayouts = week.dates.map(date => layoutBoardDay(board.events, date, shift));
    const slots = createBoardSlots(week.startDate, shift);
    const window = getBoardWindow(week.startDate, shift);
    return (
        <section className="planning-board" aria-label={`Planning board for ${board.machine}`}>
            <div className="planning-board__heading">
                <span>WEEK {moment(week.startDate).isoWeek()} · SHIFT {window.start.format('h:mmA')} – {window.end.format('h:mmA')}</span>
                <h2>Planning board for {board.machine}</h2>
            </div>
            <div className="planning-board__viewport" tabIndex={0} aria-label={`Seven-day timeline for ${board.machine}`}>
                <div className="planning-board__table" style={{ '--board-slot-count': slots.length }}>
                    <div className="planning-board__date-row">
                        <div className="planning-board__corner">Time / Date</div>
                        {week.dates.map((date, index) => (
                            <div key={date} className={`planning-board__date${moment(date).day() === 0 ? ' is-sunday' : ''}`} data-testid="planning-board-date-header">
                                <span>{moment(date).format('ddd').toUpperCase()}</span><strong>{moment(date).format('DD/MM/YYYY')}</strong>
                                {dayLayouts[index].overflow.length > 0 && <button type="button" className="planning-board__more" aria-label={`${dayLayouts[index].overflow.length} more jobs on ${board.machine} on ${moment(date).format('DD/MM/YYYY')}`} onClick={() => onOverflow({ machine: board.machine, date })}>+{dayLayouts[index].overflow.length} more</button>}
                            </div>
                        ))}
                    </div>
                    <div className="planning-board__timeline">
                        <div className="planning-board__times" aria-hidden="true">
                            {[...slots, window.end.format('YYYY-MM-DD HH:mm:ss')].map((time, index) => <span key={time} className={moment(time).minute() === 0 ? 'is-hour' : ''} style={{ top: `${index / slots.length * 100}%` }}>{moment(time).format('h:mma')}</span>)}
                        </div>
                        {week.dates.map((date, index) => <BoardDay key={date} board={board} date={date} layout={dayLayouts[index]} shift={shift} {...actions} />)}
                    </div>
                    {['Manpower', 'Target output'].map((label, index) => (
                        <div className="planning-board__metric-row" key={label}>
                            <div className="planning-board__metric-label">{label}</div>
                            {week.dates.map(date => {
                                const metrics = getBoardDayMetrics(board.events, date, shift);
                                const sunday = moment(date).day() === 0;
                                return <div key={date} className={sunday ? 'is-sunday' : ''} data-testid={sunday ? 'planning-board-sunday-metric' : undefined}>{index === 0 ? metrics.manpower ?? '—' : metrics.pending ? '—' : metrics.targetOutput}</div>;
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default function PlanningBoard({ boards, week, shift = BOARD_SHIFT, canCreate, onAddJob, onOpenJob, onDeleteJob, onMoveJob, onResizeJob }) {
    const [overflowSelection, setOverflowSelection] = useState(null);
    const [interactionError, setInteractionError] = useState('');
    const visibleBoards = boards.filter(board => week.dates.some(date => board.events.some(event => clipBoardEvent(event, date, shift))));
    const overflowBoard = overflowSelection && visibleBoards.find(board => board.machine === overflowSelection.machine);
    const overflowJobs = overflowBoard ? layoutBoardDay(overflowBoard.events, overflowSelection.date, shift).overflow : [];
    return (
        <div className="planning-boards">
            {interactionError && <div role="alert" className="planning-calendar__status">{interactionError}<button type="button" onClick={() => setInteractionError('')}>Dismiss</button></div>}
            {visibleBoards.length === 0 && <p role="status">No jobs match the selected dates and filters.</p>}
            {visibleBoards.map(board => <MachineBoard key={board.machine} board={board} week={week} shift={shift} canCreate={canCreate} onAddJob={onAddJob} onOpenJob={onOpenJob} onDeleteJob={onDeleteJob} onMoveJob={onMoveJob} onResizeJob={onResizeJob} onError={setInteractionError} onOverflow={setOverflowSelection} />)}
            <Modal show={Boolean(overflowBoard)} onHide={() => setOverflowSelection(null)} aria-labelledby="planning-board-overflow-title">
                <Modal.Header closeButton><Modal.Title id="planning-board-overflow-title">Additional jobs · {overflowSelection && overflowSelection.machine}</Modal.Title></Modal.Header>
                <Modal.Body>
                    {overflowSelection && <p>{moment(overflowSelection.date).format('DD/MM/YYYY')}</p>}
                    {overflowJobs.length === 0 && <p>No additional jobs.</p>}
                    {overflowJobs.map(({ event }) => {
                        const parsed = parsePlanningTitle(event.title);
                        return <div className="planning-board__overflow-job" key={event.oldId ?? event.id ?? event.jobID}>
                            <button type="button" className="planning-board__overflow-open" aria-label={`Open ${parsed.displayTitle}`} onClick={() => { setOverflowSelection(null); if (onOpenJob) onOpenJob(event); }}>{parsed.displayTitle}<small>{moment(event.start).format('DD/MM h:mm A')} – {moment(event.end).format('DD/MM h:mm A')} · {getPlanningManpower(event) ?? '—'} MP</small></button>
                            {canCreate && onDeleteJob && <button type="button" disabled={Boolean(getPlanningDeletionReason(event))} title={getPlanningDeletionReason(event) || 'Delete schedule'} aria-label={`Delete ${parsed.displayTitle}`} onClick={() => onDeleteJob(event)}>×</button>}
                        </div>;
                    })}
                </Modal.Body>
            </Modal>
        </div>
    );
}
